import { cn } from '@/lib/utils'
import { type LucideIcon, X } from 'lucide-react'

export interface BannerAction {
	label: string
	onClick?: () => void
	href?: string
}

export interface BannerProps {
	title?: string
	description: string
	icon?: LucideIcon
	variant?: 'info' | 'warning'
	onClose?: () => void
	actions?: BannerAction[]
	className?: string
}

export function Banner({ title, description, icon: Icon, variant = 'info', onClose, actions, className }: BannerProps) {
	const actionPaddingClass = Icon ? 'pl-[20px]' : 'pl-0'

	return (
		<div
			className={cn(
				'relative w-full rounded-md border p-3 flex flex-col gap-2 transition-all duration-200 ease-in-out',
				variant === 'info' && 'bg-info-bg border-info/20 text-info',
				variant === 'warning' && 'bg-status-warning-bg border-warning/20 text-status-warning-text',
				className,
			)}>
			{onClose && (
				<button
					type='button'
					onClick={onClose}
					className={cn(
						'absolute top-2.5 right-2.5 p-1 rounded-md transition-colors cursor-pointer flex items-center justify-center',
						variant === 'info' && 'text-info/70 hover:text-info hover:bg-info/10',
						variant === 'warning' && 'text-status-warning-text/70 hover:text-status-warning-text hover:bg-warning/10',
					)}
					aria-label='Close'>
					<X className='size-(--icon-standalone)' />
				</button>
			)}

			<div className={cn('flex items-start gap-(--spacing-icon-text)', onClose ? 'pr-6' : 'pr-0')}>
				{Icon && <Icon className='size-(--icon-standalone) shrink-0 mt-[2px]' />}
				<div className='flex flex-col gap-0.5 min-w-0'>
					{title && (
						<h4
							className={cn(
								'text-body font-semibold leading-tight min-h-4',
								variant === 'info' && 'text-info',
								variant === 'warning' && 'text-status-warning-text',
							)}>
							{title}
						</h4>
					)}
					<p
						className={cn(
							'text-control leading-relaxed',
							variant === 'info' && 'text-info/90',
							variant === 'warning' && 'text-status-warning-text/90',
						)}>
						{description}
					</p>
				</div>
			</div>

			{actions && actions.length > 0 && (
				<div className={cn('flex flex-wrap items-center gap-x-4 gap-y-1 mt-1', actionPaddingClass)}>
					{actions.map(action => {
						if (action.href) {
							return (
								<a
									key={action.label}
									href={action.href}
									onClick={action.onClick}
									className={cn(
										'text-control font-semibold hover:underline cursor-pointer transition-all',
										variant === 'info' && 'text-info',
										variant === 'warning' && 'text-status-warning-text',
									)}>
									{action.label}
								</a>
							)
						}
						return (
							<button
								key={action.label}
								type='button'
								onClick={action.onClick}
								className={cn(
									'text-control font-semibold hover:underline cursor-pointer transition-all bg-transparent border-none p-0 text-left',
									variant === 'info' && 'text-info',
									variant === 'warning' && 'text-status-warning-text',
								)}>
								{action.label}
							</button>
						)
					})}
				</div>
			)}
		</div>
	)
}
